import React from 'react';
import { Copy, Landmark, Banknote } from 'lucide-react';
import { toast } from 'sonner';

// Shared bank-transfer details block (customer payment + confirm screens).
const copyText = async (text) => {
  try { await navigator.clipboard.writeText(String(text)); toast.success('Datos copiados'); }
  catch { toast.error('No se pudo copiar. Selecciona los datos para copiarlos manualmente.'); }
};

const Line = ({ label, value, copy, field, testId }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-[11px] font-semibold text-gray-500 w-[92px] shrink-0 pt-0.5">{label}</span>
      <span data-testid={`${testId}-${field}`} className="flex-1 min-w-0 text-[13px] font-bold text-[#1E2130] [overflow-wrap:anywhere]">{value}</span>
      {copy && (
        <button
          data-testid={testId === 'bank-details' ? `copy-${label.toLowerCase().replace(/[^a-z]+/g, '-')}` : `${testId}-copy-${field}`}
          title={`Copiar ${label}`}
          onClick={() => copyText(value)}
          className="w-7 h-7 rounded-lg bg-[#F7F8FA] text-gray-500 flex items-center justify-center shrink-0 press"
        ><Copy size={13} /></button>
      )}
    </div>
  );
};

export const BankDetails = ({ bank, amount, compact = false, testId = 'bank-details', heading = 'Datos para tu transferencia' }) => {
  if (!bank) return null;
  const all = [`Banco: ${bank.name}`, bank.type && `Tipo de cuenta: ${bank.type}`, `Cuenta: ${bank.account}`, bank.holder && `Titular: ${bank.holder}`,
    bank.beneficiary && `Beneficiario: ${bank.beneficiary}`, bank.rtn && `RTN: ${bank.rtn}`, amount && `Monto: ${amount}`].filter(Boolean).join('\n');
  return (
    <div data-testid={testId} className={compact ? 'min-w-0' : 'min-w-0 rounded-2xl bg-white border border-gray-200 p-3.5'}>
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="w-9 h-9 rounded-xl text-white text-[11px] font-extrabold flex items-center justify-center shrink-0" style={{ background: bank.color || '#1E2130' }}>{bank.short || <Landmark size={16} />}</span>
        <div className="min-w-0">
          <p data-testid={`${testId}-bank`} className="font-extrabold text-[14px] [overflow-wrap:anywhere]">{bank.name}</p>
          <p className="text-[11px] text-gray-500">{heading}</p>
        </div>
      </div>
      <Line label="Negocio" value={bank.businessName} field="business" testId={testId} />
      <Line label="Tipo de cuenta" value={bank.type} field="type" testId={testId} />
      <Line label="N° de cuenta" value={bank.account} field="account" testId={testId} copy />
      <Line label="Titular" value={bank.holder} field="holder" testId={testId} copy />
      <Line label="Beneficiario" value={bank.beneficiary} field="beneficiary" testId={testId} copy />
      <Line label="RTN" value={bank.rtn} field="rtn" testId={testId} copy />
      <Line label="Monto a pagar" value={amount} field="amount" testId={testId} copy />
      {bank.instructions && <p data-testid={`${testId}-instructions`} className="mt-2 text-[11.5px] text-gray-600 bg-[#F7F8FA] rounded-xl p-2.5 leading-snug [overflow-wrap:anywhere]">{bank.instructions}</p>}
      <button data-testid={testId === 'bank-details' ? 'copy-all-bank' : `${testId}-copy-all`} onClick={() => copyText(all)} className="mt-2.5 w-full h-10 rounded-xl bg-[#1E2130] text-white text-[13px] font-bold flex items-center justify-center gap-2 press">
        <Copy size={15} /> Copiar todos los datos
      </button>
    </div>
  );
};

export const CashDetails = ({ amount, pickup = false }) => (
  <div data-testid="cash-details" className="rounded-2xl bg-[#E9F7EF] border border-[#22A45D]/25 p-3.5">
    <div className="flex items-center gap-2.5">
      <span className="w-9 h-9 rounded-xl bg-[#22A45D] text-white flex items-center justify-center shrink-0"><Banknote size={18} /></span>
      <div><p className="font-extrabold text-[14px] text-[#15603A]">Pago en efectivo</p><p className="text-[11px] text-[#15603A]/80">{pickup ? 'Pagas directamente al negocio al recoger tu pedido' : 'Pagas al conductor cuando recibas tu pedido'}</p></div>
    </div>
    {amount && <p className="mt-2.5 text-[12.5px] text-[#15603A]">Ten listos <span className="font-extrabold">{amount}</span>. {!pickup && 'El conductor puede no llevar cambio para billetes grandes.'}</p>}
  </div>
);

export default BankDetails;
